// Debug utility for focus loss investigation
// Automatically disabled in production builds

const DEBUG = process.env.NODE_ENV === 'development' || process.env.REACT_APP_DEBUG === 'true';

export const debugLog = (component: string, message: string, ...args: any[]) => {
  if (DEBUG) {
    const timestamp = new Date().toISOString().split('T')[1].split('.')[0];
    console.log(`[${timestamp}] [${component}] ${message}`, ...args);
  }
};

export const debugWarn = (component: string, message: string, ...args: any[]) => {
  if (DEBUG) {
    const timestamp = new Date().toISOString().split('T')[1].split('.')[0];
    console.warn(`[${timestamp}] [${component}] ${message}`, ...args);
  }
};

export const debugError = (component: string, message: string, ...args: any[]) => {
  if (DEBUG) {
    const timestamp = new Date().toISOString().split('T')[1].split('.')[0];
    console.error(`[${timestamp}] [${component}] ${message}`, ...args);
  }
};

// Focus tracking utilities
export const logActiveElement = (component: string, context: string) => {
  if (DEBUG) {
    const activeElement = document.activeElement;
    debugLog(component, `${context} - Active element:`, {
      tagName: activeElement?.tagName,
      id: activeElement?.id,
      className: activeElement?.className,
      type: (activeElement as HTMLInputElement)?.type,
      value: (activeElement as HTMLInputElement)?.value
    });
  }
};

export const isDebugEnabled = () => DEBUG;

// Global focus tracking
let focusHistory: Array<{ 
  timestamp: string; 
  element: string; 
  context: string; 
  component: string;
}> = [];

export const trackFocusChange = (component: string, context: string) => {
  if (!DEBUG) return;
  
  const activeElement = document.activeElement;
  const elementInfo = activeElement ? 
    `${activeElement.tagName}#${activeElement.id || 'no-id'}.${activeElement.className || 'no-class'}` : 
    'null';
    
  focusHistory.push({
    timestamp: new Date().toISOString(),
    element: elementInfo,
    context,
    component
  });
  
  // Keep only last 50 entries
  if (focusHistory.length > 50) {
    focusHistory = focusHistory.slice(-50);
  }
  
  debugLog(component, `Focus tracked: ${context}`, { activeElement: elementInfo });
};

export const getFocusHistory = () => focusHistory;

export const logFocusHistory = (component: string) => {
  if (DEBUG && focusHistory.length > 0) {
    debugLog(component, 'Focus History (last 10):', focusHistory.slice(-10));
  }
};

// Monitor all focus changes on the document
if (DEBUG && typeof document !== 'undefined') {
  let lastActiveElement: Element | null = null;
  
  const checkFocusChange = () => {
    if (document.activeElement !== lastActiveElement) {
      const oldElement = lastActiveElement ? 
        `${lastActiveElement.tagName}#${lastActiveElement.id || 'no-id'}` : 'null';
      const newElement = document.activeElement ? 
        `${document.activeElement.tagName}#${document.activeElement.id || 'no-id'}` : 'null';
        
      debugWarn('GlobalFocusMonitor', `Focus changed: ${oldElement} -> ${newElement}`);
      
      lastActiveElement = document.activeElement;
    }
  };
  
  // Check focus changes every 100ms
  setInterval(checkFocusChange, 100);
  
  // Also listen to focus events
  document.addEventListener('focusin', (e) => {
    debugLog('GlobalFocusMonitor', `Focus IN event`, { 
      target: e.target ? `${(e.target as Element).tagName}#${(e.target as Element).id || 'no-id'}` : 'null'
    });
  });
  
  document.addEventListener('focusout', (e) => {
    debugLog('GlobalFocusMonitor', `Focus OUT event`, { 
      target: e.target ? `${(e.target as Element).tagName}#${(e.target as Element).id || 'no-id'}` : 'null'
    });
  });
}